/**
 * Handles the "nudge the customer to rate Carata on the Play/App Store" flow.
 *
 * Trigger points (see TrackOrder.js):
 *   - An order the customer is viewing reaches "completed" status.
 *   - The customer submits a review for one of their orders.
 *
 * Once the customer has placed/completed at least REQUIRED_COMPLETED_ORDERS
 * orders, and hasn't already responded to the nudge, we show a small modal
 * asking them to rate the app. "Rate Now" triggers the native in-app review
 * dialog (Capacitor's `@capacitor-community/in-app-review`) and permanently
 * silences the nudge. "Maybe Later" snoozes it for REMIND_LATER_DAYS days.
 */
import { InAppReview } from "@capacitor-community/in-app-review";
import { app } from "modular-openscriptjs";
import { store, getStore, sendEvent, consoleErrorOnLocal } from "./helpers";

const STORAGE_KEY = "app_review_prompt_state";
const REQUIRED_COMPLETED_ORDERS = 5;
const REMIND_LATER_DAYS = 3;
const MAX_TRACKED_ORDER_IDS = 20;

const defaultState = () => ({
    completedOrderIds: [],
    completedOrderCount: 0,
    responded: false,
    laterUntil: null,
});

async function getState() {
    const state = await getStore(STORAGE_KEY);
    return { ...defaultState(), ...(state || {}) };
}

async function saveState(state) {
    await store(STORAGE_KEY, state);
}

function isNativePlatform() {
    return typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();
}

/**
 * Call this whenever the customer lands on a completed order. Increments the
 * (deduped) completed order counter, then checks if it's time to nudge them.
 * @param {number|string} orderId
 */
export async function trackCompletedOrderForReview(orderId) {
    if (!isNativePlatform() || !orderId) return;

    const state = await getState();

    if (!state.completedOrderIds.includes(orderId)) {
        state.completedOrderIds.push(orderId);
        // keep the tracked id list small, we only need it for de-duping
        if (state.completedOrderIds.length > MAX_TRACKED_ORDER_IDS) {
            state.completedOrderIds = state.completedOrderIds.slice(
                -MAX_TRACKED_ORDER_IDS,
            );
        }
        state.completedOrderCount += 1;
        await saveState(state);
    }

    await maybeTriggerAppReviewPrompt(state);
}

/**
 * Checks eligibility and, if the customer qualifies, broadcasts the event
 * that opens the review nudge modal.
 * @param {object} [preloadedState]
 */
export async function maybeTriggerAppReviewPrompt(preloadedState = null) {
    if (!isNativePlatform()) return;

    const state = preloadedState ?? (await getState());

    if (state.responded) return;
    if (state.completedOrderCount < REQUIRED_COMPLETED_ORDERS) return;
    if (state.laterUntil && Date.now() < state.laterUntil) return;

    sendEvent(app("$e").appReview.needs.prompt);
}

/**
 * Called when the customer taps "Rate Now" on the nudge modal.
 */
export async function acceptAppReviewPrompt() {
    const state = await getState();
    state.responded = true;
    await saveState(state);

    try {
        await InAppReview.requestReview();
    } catch (e) {
        consoleErrorOnLocal("InAppReview.requestReview failed", e);
    }
}

/**
 * Called when the customer taps "Maybe Later" (or dismisses) the nudge modal.
 */
export async function snoozeAppReviewPrompt() {
    const state = await getState();
    state.laterUntil = Date.now() + REMIND_LATER_DAYS * 24 * 60 * 60 * 1000;
    await saveState(state);
}
